import { Location } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { escape } from 'lodash';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs';
import { UiModule } from '../../../../components/ui.module';
import { defaultTableDefOptions } from '../../../../helpers';
import { LocationService } from '../../../../service/location.service';
import { ReviewActivityEntry } from '../../../../types';

export function describeReviewActivity(entry: ReviewActivityEntry): string {
  if (entry.type === 'weekly_reviews') {
    return entry.from === null
      ? `<strong>Weekly Reviews</strong> was added ${entry.to} reviews`
      : `<strong>Weekly Reviews</strong> was updated from ${entry.from} to ${entry.to}`;
  }
  if (!entry.to) {
    return '<strong>Review Notes</strong> were cleared';
  }
  return `<strong>Review Notes</strong> was ${entry.from ? 'updated' : 'added'} "${escape(entry.to)}"`;
}

@Component({
  selector: 'app-review-activity',
  imports: [UiModule, AgGridAngular],
  templateUrl: './review-activity.component.html',
})
export class ReviewActivityComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private locationService = inject(LocationService);

  public isLoading = signal(false);
  public entries = signal<ReviewActivityEntry[]>([]);
  private branchName = signal('');
  private locationName = signal('');

  public subheader = computed(() => (this.branchName() && this.locationName() ? `${this.branchName()} - ${this.locationName()}` : ''));

  public readonly tableDef: GridOptions<ReviewActivityEntry> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columnDefs: ColDef<ReviewActivityEntry>[] = [
    {
      field: 'happened_at',
      headerName: 'Happened At',
      maxWidth: 240,
      valueFormatter: (params) =>
        params.value ? DateTime.fromISO(params.value, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) : '',
    },
    {
      field: 'changed_by',
      headerName: 'Changed By',
      maxWidth: 360,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Change Log',
      flex: 1,
      autoHeight: true,
      cellRenderer: (params: { data: ReviewActivityEntry }) =>
        `<div class="whitespace-break-spaces leading-5">${describeReviewActivity(params.data)}</div>`,
    },
  ];

  public ngOnInit() {
    const locationId = Number(this.route.snapshot.paramMap.get('locationId'));
    if (!locationId) return;

    this.isLoading.set(true);
    this.locationService
      .getLocationReviewActivity(locationId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((activity) => {
        this.branchName.set(activity.branch_name);
        this.locationName.set(activity.location_name);
        this.entries.set(activity.items);
      });
  }

  public back() {
    this.location.back();
  }
}
